/**
 * CustomerAutocomplete Component
 * 
 * Async autocomplete for selecting customers.
 * Uses the generic AsyncAutocomplete component (DRY).
 */
import { useCallback } from 'react';
import { Customer } from 'types';
import { useApi } from 'api';
import AsyncAutocomplete from './AsyncAutocomplete';

interface Props {
  value: Customer | null;
  onChange: (customer: Customer | null) => void;
}

/**
 * Get display label for a customer
 */
const getCustomerLabel = (customer: Customer): string => {
  return `${customer.first_name} ${customer.last_name}`;
};

const CustomerAutocomplete = ({ value, onChange }: Props) => {
  const api = useApi();

  const fetchCustomers = useCallback(
    async (query: string, page: number) => {
      const { data } = await api.getSearchCustomers({
        query,
        per_page: 10,
        page,
      });

      return {
        items: data.customers,
        hasMore: data.pagination.page < data.pagination.total_pages,
      };
    },
    [api]
  );

  return (
    <AsyncAutocomplete<Customer>
      value={value}
      onChange={onChange}
      fetchItems={fetchCustomers}
      getLabel={getCustomerLabel}
      placeholder="Search a customer"
    />
  );
};

export default CustomerAutocomplete;
